/** Pure decision logic for the router-wide error boundary, kept dependency-free so the branches are unit-testable. */

// The same prefix sets the proactive `_app` guard uses, so the reactive net and the guard cannot drift apart.
import { AUTH_REQUIRED_ROUTE_PREFIXES, TEST_GATED_ROUTE_PREFIXES } from './route-access'

// The cached `getUser` shape the classifier reads (authOptional → null when logged out).
export interface RouteErrorUser {
  type?: string
  testPassed?: boolean | null
  testPassedValues?: boolean | null
  testPassedPersonality?: boolean | null
}

// One event, several wordings, which is why this is not shorter. Chrome says `Failed to fetch
// dynamically imported module`, Firefox `error loading dynamically imported module`, Safari `Importing
// a module script failed` — and WebKit also reports the same failed cross-origin module fetch as
// `Cross-origin script load denied by Cross-Origin Resource Sharing policy`, which is NOT a CORS
// misconfiguration of ours. Covering only some wordings is how the identical mechanism keeps reaching
// the issue board through whichever browser was left out, so add a wording rather than a second rule.
const CHUNK_LOAD_MESSAGE =
  /dynamically imported module|imported module script failed|importing a module script failed|cross-origin script load denied|ChunkLoadError|Loading chunk/i

/** Three covers the wrappers seen in practice and keeps a cyclic or absurdly deep chain from costing anything. */
const MAX_CAUSE_DEPTH = 3

/**
 * Did a CODE chunk fail to load? Its own branch because the browser CACHES the rejection — only a full reload
 * re-fetches — and because it is usually not a bug: hashed assets persist on the CDN, so the common cause is the
 * network. Matches the dynamic-import wordings only, and deliberately NOT a bare "Failed to fetch": that is also
 * what a failed tRPC DATA query throws offline, and a data error must recover in place.
 */
export function isChunkLoadError(error: unknown): boolean {
  // Down the `cause` chain: an import that fails during concurrent rendering surfaces as a generic minified React
  // error with the real failure demoted to `cause`, which read as a React fault and ticketed a deploy as a bug.
  let current: unknown = error
  for (let depth = 0; depth <= MAX_CAUSE_DEPTH; depth++) {
    const message = current instanceof Error ? current.message : typeof current === 'string' ? current : ''
    if (CHUNK_LOAD_MESSAGE.test(message)) return true
    if (!(current instanceof Error)) return false
    current = current.cause
    if (current == null) return false
  }
  return false
}

/**
 * Key off the absent session / pending test rather than the error code: a DEFERRED-streamed query's rejection has
 * its code REDACTED in production. An explicit TEST_INCOMPLETE message also counts.
 */
export function classifyRouteError(input: {
  routeId: string
  cachedUser: RouteErrorUser | null
  message?: string | undefined
}): { isAuth: boolean; isTestIncomplete: boolean } {
  const { routeId, cachedUser, message } = input
  const hasUser = cachedUser != null
  const needsAuth = AUTH_REQUIRED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isAuth = !hasUser && needsAuth
  const applicantTestPending =
    cachedUser?.type === 'applicant' &&
    !(cachedUser.testPassed && cachedUser.testPassedValues && cachedUser.testPassedPersonality)
  const onTestGatedRoute = TEST_GATED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isTestIncomplete = message === 'TEST_INCOMPLETE' || (applicantTestPending && onTestGatedRoute)
  return { isAuth, isTestIncomplete }
}
